// NavRail der People-App (DESIGN §3.1.2): Monogramm oben, Einträge mit Icon-Kachel 36 und Label,
// unten „Bar AI“ und Hilfe. Aktiv: Kachel `card`, Icon und Label `accent-fg`.
import { type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import { Monogram } from '../kern/Monogram';
import { CountBadge, useRovingFocus } from './parts';

export interface ShellNavItem {
  id: string;
  label: string;
  icon: PhosphorIcon;
  href: string;
  active?: boolean;
  /** Zähler oben rechts an der Kachel. */
  badge?: number;
  badgeTone?: 'notify' | 'unread';
  /** Volle Navigation (andere Plattform). */
  reload?: boolean;
  /** Icon-Farbe außerhalb des aktiven Zustands, z. B. „Bar AI“ in `ai`. */
  tint?: 'ai';
  /** Statt Link ein Knopf (z. B. Hilfe öffnet die Befehlspalette). */
  onSelect?: () => void;
}

export interface NavRailProps {
  items: readonly ShellNavItem[];
  bottomItems?: readonly ShellNavItem[];
  homeHref?: string;
  appName?: string;
  className?: string;
}

function RailEntry({ item }: { item: ShellNavItem }) {
  const Glyph = item.icon;
  const classes = cn(
    // Fokusring an der Kachel 36 (Abstand 2), nicht am ganzen Eintrag (DESIGN §3.0).
    'group flex w-21 flex-col items-center gap-1.5 rounded-md py-1 text-nav focus-visible:outline-none',
    item.active ? 'text-accent-fg' : 'text-fg-nav hover:text-fg',
  );
  const name = item.badge ? `${item.label}, ${item.badge} unread` : item.label;
  const body = (
    <>
      <span
        className={cn(
          'relative flex size-9 items-center justify-center rounded-md transition-colors duration-(--bar-dur-fast) group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-focus',
          item.active ? 'bg-card' : 'group-hover:bg-card-hover',
          !item.active && item.tint === 'ai' && 'text-ai',
        )}
      >
        <Glyph size={22} weight="fill" aria-hidden />
        {item.badge ? (
          <CountBadge
            count={item.badge}
            tone={item.badgeTone}
            className="absolute -top-1.5 -right-2 ring-2 ring-page"
          />
        ) : null}
      </span>
      <span className="max-w-full truncate">{item.label}</span>
    </>
  );
  if (item.onSelect) {
    return (
      <button
        type="button"
        data-roving
        aria-label={name}
        className={classes}
        onClick={item.onSelect}
      >
        {body}
      </button>
    );
  }
  return (
    <RouterLink
      href={item.href}
      reload={item.reload}
      data-roving
      aria-label={name}
      aria-current={item.active ? 'page' : undefined}
      className={classes}
    >
      {body}
    </RouterLink>
  );
}

export function NavRail({
  items,
  bottomItems = [],
  homeHref = '/',
  appName = 'Bar',
  className,
}: NavRailProps) {
  const roving = useRovingFocus<HTMLElement>();
  return (
    <nav
      ref={roving}
      aria-label="Main"
      className={cn(
        'fixed inset-y-0 left-0 z-(--bar-z-nav) hidden w-rail flex-col items-center bg-page pt-4 pb-4 md:flex',
        className,
      )}
    >
      <RouterLink href={homeHref} aria-label={`${appName} home`} className="mb-4 rounded-md">
        <Monogram size={36} appName={appName} />
      </RouterLink>
      {/* Mittenabstand der Einträge 74 (S1, DESIGN §3.1.2): Eintrag 64 + Abstand 10. */}
      <ul className="flex min-h-0 flex-1 flex-col items-center gap-2.5 overflow-y-auto [scrollbar-width:none]">
        {items.map((item) => (
          <li key={item.id}>
            <RailEntry item={item} />
          </li>
        ))}
      </ul>
      {bottomItems.length > 0 ? (
        <ul className="mt-4 flex flex-col items-center gap-2.5">
          {bottomItems.map((item) => (
            <li key={item.id}>
              <RailEntry item={item} />
            </li>
          ))}
        </ul>
      ) : null}
    </nav>
  );
}
